import { Mic, Brain, History, Mail, Copy, Stethoscope } from "lucide-react";

export default function Features() {
  return (
    <section
      id="features"
      className="bg-white py-20 md:py-32 border-y border-slate-100"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <span className="font-figtree text-xs uppercase tracking-[0.22em] font-semibold text-[#059669]">
            Features
          </span>
          <h2 className="mt-3 font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0F172A] leading-[1.1]">
            Everything a physio actually needs.
          </h2>
          <p className="mt-4 font-figtree text-base sm:text-lg text-[#475569] leading-relaxed">
            No bloat, no twelve-tab dashboards. Just the tools that make
            documentation disappear.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {/* Big hero card */}
          <div
            className="md:col-span-4 md:row-span-2 rounded-3xl bg-gradient-to-br from-[#064E3B] to-[#065F46] text-white p-8 md:p-10 relative overflow-hidden shadow-lift-hover"
            data-testid="feature-voice-dictation"
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#10B981]/30 blur-3xl" />
            <Mic className="w-7 h-7 text-[#86EFAC] mb-6 relative" />
            <h3 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight relative">
              Voice dictation that speaks physio.
            </h3>
            <p className="mt-3 font-figtree text-[#D1FAE5]/90 max-w-md relative">
              Browser-native Web Speech transcribes you live. ROM, end-feel,
              MMT — clinical terminology stays intact.
            </p>

            <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10 relative">
              <div className="font-figtree text-[10px] uppercase tracking-[0.18em] text-[#86EFAC] font-semibold mb-2">
                Live transcript
              </div>
              <p className="font-figtree text-sm text-white/90 leading-relaxed">
                "Objective: range of motion improved to 110 degrees flexion.
                Strength 4/5. Assessment: post-op ACL recovery on track..."
              </p>
            </div>
          </div>

          {/* AI extraction */}
          <div
            className="md:col-span-2 rounded-3xl bg-[#F0FDF4] p-7 shadow-lift-hover border border-[#D1FAE5]"
            data-testid="feature-ai-extraction"
          >
            <Brain className="w-6 h-6 text-[#059669] mb-5" />
            <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              AI SOAP extraction
            </h3>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              Claude Sonnet 4.5 separates your speech into Assessment, Objective,
              Goals and History — automatically.
            </p>
          </div>

          {/* Patient history */}
          <div
            className="md:col-span-2 rounded-3xl bg-white p-7 shadow-soft shadow-lift-hover border border-slate-100"
            data-testid="feature-patient-history"
          >
            <History className="w-6 h-6 text-[#059669] mb-5" />
            <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              Full patient timeline
            </h3>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              Every plan, every visit, ordered by date — read-only, audit-safe,
              one click away.
            </p>
          </div>

          {/* Email export */}
          <div
            className="md:col-span-2 rounded-3xl bg-white p-7 shadow-soft shadow-lift-hover border border-slate-100"
            data-testid="feature-email-export"
          >
            <Mail className="w-6 h-6 text-[#059669] mb-5" />
            <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              One-click email
            </h3>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              Send the full formatted note to your inbox so you can paste it
              into any EMR you already use.
            </p>
          </div>

          {/* Copy fields */}
          <div
            className="md:col-span-2 rounded-3xl bg-white p-7 shadow-soft shadow-lift-hover border border-slate-100"
            data-testid="feature-copy-fields"
          >
            <Copy className="w-6 h-6 text-[#059669] mb-5" />
            <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              Copy-per-field
            </h3>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              Tiny copy buttons on every field so TherapyNote slots beside your
              main app — not in front of it.
            </p>
          </div>

          {/* Reports */}
          <div
            className="md:col-span-2 rounded-3xl bg-white p-7 shadow-soft shadow-lift-hover border border-slate-100"
            data-testid="feature-reports"
          >
            <Stethoscope className="w-6 h-6 text-[#059669] mb-5" />
            <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              Clinic-wide reports
            </h3>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              Filter by day, week, month or custom range. Initials and IDs only —
              never full names — for privacy by default.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
